import React, { useState, useEffect, useRef } from "react";

import {
  Banner,
  Modal,
  ResourceItem,
  ResourceList,
  Spinner,
  Stack,
  TextField,
  Thumbnail,
  TextStyle,
} from "@shopify/polaris";

import { fetcher, handleErrors } from "util/fetch";
import { useAdmin } from "context/admin";

/**
 * Product picker modal for the Weight Options page.
 *
 * Lets the merchant search the shop's product catalogue, pick a product, and
 * confirm (or correct) the inferred base PLU before handing off to the parent.
 *
 * Props
 * -----
 * @param {boolean}  open      - Whether the modal is visible.
 * @param {function} onClose   - Called with no arguments when the modal should close.
 * @param {function} onSelect  - Called with `{ plu, productTitle, productImage }`
 *                               when the merchant confirms a selection.
 *                               Returning a Promise is NOT expected here — the
 *                               parent is responsible for closing the modal after
 *                               the selection is processed.
 */
export default function ProductPickerModal({ open, onClose, onSelect }) {
  const { selectedShop } = useAdmin();

  // Search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = not searched yet
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Selection state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [plu, setPlu] = useState("");

  // Ref for debounce timer
  const debounceTimerRef = useRef(null);
  const searchRequestSeqRef = useRef(0);

  // Re-seed state every time the modal opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults(null);
      setLoading(false);
      setFetchError(null);
      setSelectedProduct(null);
      setPlu("");
    }
  }, [open]);

  // Debounced search: fire 250ms after the user stops typing.
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = query.trim();
    const requestSeq = ++searchRequestSeqRef.current;

    if (!trimmed) {
      // Clear results when the query is empty.
      setResults(null);
      setFetchError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFetchError(null);

    debounceTimerRef.current = setTimeout(() => {
      if (!selectedShop) {
        if (requestSeq === searchRequestSeqRef.current) {
          setLoading(false);
        }
        return;
      }

      fetcher(
        `/api/shops/${selectedShop.id}/products?query=${encodeURIComponent(
          trimmed
        )}&per_page=20`
      )
        .then(handleErrors)
        .then((r) => r.json())
        .then((data) => {
          if (requestSeq !== searchRequestSeqRef.current) return;
          setResults(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch((err) => {
          if (requestSeq !== searchRequestSeqRef.current) return;
          setFetchError(err.message || "An error occurred while searching.");
          setLoading(false);
        });
    }, 250);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedShop]);

  // Infer the base PLU from the first non-blank SKU in a product's variants.
  const inferPlu = (product) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstVariant = variants.find(
      (v) => v.sku && String(v.sku).trim() !== ""
    );
    if (!firstVariant) return "";
    return String(firstVariant.sku).split("-")[0];
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setPlu(inferPlu(product));
  };

  const handleUseProduct = () => {
    if (!selectedProduct) return;
    const trimmedPlu = plu.trim();
    if (!trimmedPlu) return;

    const productImage =
      selectedProduct.image && selectedProduct.image.src
        ? selectedProduct.image.src
        : null;

    onSelect({
      plu: trimmedPlu,
      productTitle: selectedProduct.title || "",
      productImage,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const canUseProduct = Boolean(
    selectedProduct && plu && plu.trim() !== ""
  );

  // Determine body content
  const renderBody = () => {
    if (fetchError) {
      return (
        <Modal.Section>
          <Banner status="critical">{fetchError}</Banner>
        </Modal.Section>
      );
    }

    if (loading) {
      return (
        <Modal.Section>
          <Stack distribution="center">
            <Spinner size="small" />
          </Stack>
        </Modal.Section>
      );
    }

    if (results === null) {
      return (
        <Modal.Section>
          <TextStyle variation="subdued">
            Type to search by product title, SKU, or barcode.
          </TextStyle>
        </Modal.Section>
      );
    }

    if (results.length === 0) {
      return (
        <Modal.Section>
          <TextStyle variation="subdued">
            No products match your search.
          </TextStyle>
        </Modal.Section>
      );
    }

    return (
      <>
        {results.length >= 20 && (
          <Modal.Section>
            <TextStyle variation="subdued">
              {/* The endpoint does not return a total count, so this is an approximation. */}
              Showing first 20 results — refine your search to narrow down.
            </TextStyle>
          </Modal.Section>
        )}
        <ResourceList
          resourceName={{ singular: "product", plural: "products" }}
          items={results}
          renderItem={(product) => {
            const imageUrl =
              product.image && product.image.src ? product.image.src : null;
            const firstVariantSku = (() => {
              const variants = Array.isArray(product.variants)
                ? product.variants
                : [];
              const v = variants.find(
                (v) => v.sku && String(v.sku).trim() !== ""
              );
              return v ? v.sku : null;
            })();

            const isSelected =
              selectedProduct && selectedProduct.id === product.id;

            return (
              <ResourceItem
                id={String(product.id)}
                onClick={() => handleSelectProduct(product)}
                media={
                  imageUrl ? (
                    <Thumbnail
                      source={imageUrl}
                      alt={product.title || ""}
                      size="small"
                    />
                  ) : undefined
                }
                name={product.title || ""}
                accessibilityLabel={`Select ${product.title}`}
              >
                <Stack vertical spacing="extraTight">
                  <TextStyle variation={isSelected ? "strong" : undefined}>
                    {product.title}
                  </TextStyle>
                  {firstVariantSku && (
                    <TextStyle variation="subdued">
                      SKU: {firstVariantSku}
                    </TextStyle>
                  )}
                </Stack>
              </ResourceItem>
            );
          }}
        />
      </>
    );
  };

  return (
    <Modal
      open={open}
      title="Select a product"
      onClose={handleClose}
      primaryAction={{
        content: "Use this product",
        onAction: handleUseProduct,
        disabled: !canUseProduct,
      }}
      secondaryActions={[
        {
          content: "Cancel",
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <Stack vertical spacing="tight">
          <TextField
            label="Search by product title, SKU, or barcode"
            placeholder="e.g. Chicken breast, 00025015600000"
            value={query}
            onChange={setQuery}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setQuery("")}
          />

          {selectedProduct && (
            <TextField
              label="Base PLU"
              helpText="The base PLU key for this override. Edit if the inferred value is incorrect."
              value={plu}
              onChange={setPlu}
              autoComplete="off"
              placeholder="e.g. 00025015600000"
            />
          )}
        </Stack>
      </Modal.Section>

      {renderBody()}
    </Modal>
  );
}
